import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { delay } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-audiences-field', {
  title: 'Auditoires*',
  placeholder: 'Rechercher une audience',
  choose: 'Choisir',
  audiences: {
    general: 'Général',
    toddler: 'Tout-Petit',
    child: 'Enfant',
    teen: 'Adolescent',
    adult: 'Adulte',
    senior: 'Aîné',
    family: 'Familial',
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-audiences-field', {
  title: 'Audiences*',
  placeholder: 'Search for a audience',
  choose: 'Choose',
  audiences: {
    general: 'General',
    toddler: 'Toddler',
    child: 'Child',
    teen: 'Teen',
    adult: 'Adult',
    senior: 'Senior',
    family: 'Family',
  },
})

export interface LvcAudiencesField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcAudiencesField extends MinzeElement {
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['error', false],
  ]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'error', 'default-value']

  private audiences = [
    { id: 'general', label: Locale.getKey('lvc-audiences-field.audiences.general') },
    { id: 'toddler', label: Locale.getKey('lvc-audiences-field.audiences.toddler') },
    { id: 'child', label: Locale.getKey('lvc-audiences-field.audiences.child') },
    { id: 'teen', label: Locale.getKey('lvc-audiences-field.audiences.teen') },
    { id: 'adult', label: Locale.getKey('lvc-audiences-field.audiences.adult') },
    { id: 'senior', label: Locale.getKey('lvc-audiences-field.audiences.senior') },
    { id: 'family', label: Locale.getKey('lvc-audiences-field.audiences.family') },
  ]

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map(
        (k: any) => `${k.id}`
      )
    }
    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onAudienceClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }
    let index = this.selectedIds.indexOf(value)

    if (index > -1) {
      this.selectedIds.splice(index, 1)
    } else {
      this.selectedIds.push(value)
    }

    this.serializeValue()

    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let objects = this.audiences.filter(k => this.selectedIds.indexOf(k.id) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('audiences-field:change', this.value)
  }

  getAudiencesLabel() {
    if (this.selectedIds.length === 0) {
      return Locale.getKey('lvc-audiences-field.choose')
    }

    let labels = this.audiences.filter(k => this.selectedIds.indexOf(k.id) > -1).map(k => k.label)

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderAudience = (audience: any) => {
    if (this.filter && audience.label.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedIds.indexOf(`${audience.id}`) == -1

    return `
      <button 
        name="audience"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${audience.id}">
        ${audience.label}
        ${isNotSelected ? '' : `<lvc-circle-check-select-icon data-id="${audience.id}"></lvc-circle-check-select-icon>`}
      </button>
    `
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0

    return `
      <div class="title">${this.title || Locale.getKey('lvc-audiences-field.title')}</div>
      <div class="input ${this.selectedIds?.length ? 'has-value' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getAudiencesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${this.filter}" placeholder="${this.placeholder || Locale.getKey('lvc-audiences-field.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="audiences" data-selected="${this.selectedIds.length}">
            ${this.audiences.map(audience => this.renderAudience(audience)).join('')}
          </div>
        </div> 
      </div>
      <lvc-box>
        ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      </lvc-box>
    `
  }

  css = () => {
    return `
      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: #1E007D;
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid #1E007D;
        background: #BFF0FF;
      }

      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: #1E007D;
        background: transparent;
      }

      .title {
        color: #1E007D;
        font-family: ${Font.family};
        font-size: 14px;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid #1E007D;
        position: relative;
        border-radius: 24px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .audiences {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }

      .audiences button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: #1E007D;
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .audiences button:last-child {
        border-bottom: none;
      }

      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: #1E007D;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid #1E007D;
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }
    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="audience"]', 'click', this.onAudienceClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
